import { useMemo } from "react"

import { Table } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import EmptyComponent from "../../../../Components/Empty"
import { granularityTag } from "../../charts/axis"
import HorizontalBars from "../../charts/HorizontalBars"
import { SERIES_COLORS } from "../../charts/palette"
import TrendLineChart from "../../charts/TrendLineChart"
import ChangeBadge from "../../components/Badges/ChangeBadge"
import MerchantCell from "../../components/cells/MerchantCell"
import KpiBeside from "../../components/KpiBeside/KpiBeside"
import { listPagination, listPaginationClass } from "../../components/ListPagination/listPagination"
import MetricCard from "../../components/MetricCard/MetricCard"
import { Panel, PanelRow } from "../../components/Panel/Panel"
import { CHANNEL_OPTIONS, PROGRAM_OPTIONS } from "../../constants"
import { useCardData, useListData } from "../../hooks/useCardData"
import { getPointChange, GOOD_DIRECTION } from "../../utils/change"
import { formatCount, formatShare } from "../../utils/formats"

import rowLines from "../../components/RowLines/rowLines.module.less"

// The card's Tooltip terms, verbatim from the spec (§5 Match rate).
const INFO = [
  {
    name: "Matched",
    formula:
      "Merchant resolved by id + descriptor found (full match first, then prefix) + the descriptor has a record in the lookup's program. Descriptor status does not matter"
  },
  { name: "Match rate", formula: "Matched ÷ all lookups × 100" },
  {
    name: "Among known merchants",
    formula: "Matched ÷ lookups whose merchant was resolved on arrival × 100"
  },
  {
    name: "Change",
    formula: "In percentage points against the comparison period, for both rates"
  },
  { name: "By merchant", formula: "Matched lookups of the merchant ÷ all its lookups × 100" }
]

// The By channel axis: 0% – 50% – 100% (DAA-5-04).
const RATE_AXIS = { min: 0, max: 100, step: 50 }

// Bar labels and the trend's y-axis read whole percents, the trend's hover and last-value labels one
// decimal (DAA-5-04, DAA-5-05).
const formatWholeRate = (value) => formatShare(value, 0)

// Match rate (DAA-5-01 … DAA-5-07): what share of lookups was attributed to a descriptor of a
// merchant — both rates, the rate per channel group beside them, the trend per program and every
// merchant by its unmatched lookups, a page at a time (CHA1-4558). Every filter reaches it.
const MatchRateCard = () => {
  const { t } = useTranslation()
  const { data, loading } = useCardData("matchRate")
  const worstMerchants = useListData("worstMerchants")
  const kpis = data?.kpis
  const comparison = data?.comparison
  const tiles = useMemo(() => {
    // Change badges only while comparing (DAA-2-22), in pp; a rise is good.
    const badge = (value, previous) =>
      comparison ? (
        <ChangeBadge change={getPointChange(value, previous, GOOD_DIRECTION.UP)} />
      ) : null

    return [
      {
        key: "matchRate",
        label: t("Match rate"),
        value: kpis?.match_rate,
        decimals: 1,
        suffix: "%",
        badge: badge(kpis?.match_rate, comparison?.match_rate),
        secondary: comparison
          ? `vs ${formatShare(comparison.match_rate)} ${t("prev. period")}`
          : null,
        note: kpis
          ? `${formatCount(kpis.matched)} ${t("of")} ${formatCount(kpis.lookups)} ${t("lookups")}`
          : null
      },
      {
        key: "knownMatchRate",
        label: t("Among known merchants"),
        value: kpis?.known_match_rate,
        decimals: 1,
        suffix: "%",
        badge: badge(kpis?.known_match_rate, comparison?.known_match_rate),
        secondary: comparison
          ? `vs ${formatShare(comparison.known_match_rate)} ${t("prev. period")}`
          : null,
        note: t("merchant resolved on arrival")
      }
    ]
  }, [kpis, comparison, t])

  // In the order of the Channels filter; a group without lookups has no rate and no bar (DAA-5-04).
  const channelRows = useMemo(() => {
    const rateByChannel = data?.by_channel ?? {}

    return CHANNEL_OPTIONS.filter((option) => option.id in rateByChannel).map((option) => ({
      key: option.id,
      label: t(option.name),
      color: SERIES_COLORS[option.id],
      value: rateByChannel[option.id]
    }))
  }, [data, t])
  // Only the programs with lookups have a line, so a Program filter leaves its own (DAA-5-05).
  const programSeries = useMemo(() => {
    const ratesByProgram = data?.trend ?? {}

    return PROGRAM_OPTIONS.filter((option) => option.value in ratesByProgram).map((option) => ({
      key: option.value,
      label: option.label,
      color: SERIES_COLORS[option.value],
      data: ratesByProgram[option.value]
    }))
  }, [data])

  // antd calls a column's render with (value, row, index); the formatters take the value alone.
  const columns = useMemo(
    () => [
      {
        title: t("Merchant"),
        dataIndex: "name",
        // About twice a number column, as the design gives the names room.
        width: "34%",
        render: (name) => <MerchantCell name={name} />
      },
      {
        title: t("Lookups"),
        dataIndex: "lookups",
        align: "right",
        render: (count) => formatCount(count)
      },
      {
        title: t("Matched"),
        dataIndex: "matched",
        align: "right",
        render: (count) => formatCount(count)
      },
      {
        title: t("Not matched"),
        dataIndex: "not_matched",
        align: "right",
        render: (count) => formatCount(count)
      },
      {
        title: t("Match rate"),
        dataIndex: "match_rate",
        align: "right",
        render: (rate) => formatShare(rate)
      }
    ],
    [t]
  )
  const merchants = worstMerchants.data?.results ?? []

  return (
    <MetricCard title={t("Match rate")} info={INFO} loading={loading}>
      <KpiBeside tiles={tiles} title={t("By channel")}>
        {data && <HorizontalBars rows={channelRows} xFormat={formatWholeRate} axis={RATE_AXIS} />}
      </KpiBeside>
      {/* The panels keep their place until the first answer, so the page does not jump under them. */}
      <PanelRow>
        <Panel title={t("Trend")} tag={data && t(granularityTag(data.granularity))}>
          <div style={{ height: 260 }}>
            {data && (
              <TrendLineChart
                labels={data.labels}
                series={programSeries}
                lastIntervalIncomplete={data.last_interval_incomplete}
                yFormat={formatShare}
                yTickFormat={formatWholeRate}
                zoomY
                yMax={100}
                yMinStep={1}
                legend
              />
            )}
          </div>
        </Panel>
      </PanelRow>
      <PanelRow>
        <Panel title={t("Worst merchants")} tag={t("all merchants · sorted by not matched")}>
          {worstMerchants.data && (
            <Table
              className={cls("chart-table", rowLines.rowLines, listPaginationClass)}
              rowKey="business_account_id"
              dataSource={merchants}
              columns={columns}
              tableLayout="fixed"
              showHeader={merchants.length > 0}
              // A page change spins the table alone; under the card's own spinner it stays still.
              loading={worstMerchants.loading && !loading}
              pagination={listPagination(worstMerchants, t)}
              // Below 560px the five columns scroll sideways instead of squeezing the names.
              scroll={merchants.length > 0 ? { x: 560 } : undefined}
              locale={{ emptyText: <EmptyComponent type="no_data_available" /> }}
            />
          )}
        </Panel>
      </PanelRow>
    </MetricCard>
  )
}

export default MatchRateCard
